import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
  Chip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ClearIcon from '@material-ui/icons/Clear';
import VisibilityIcon from '@material-ui/icons/Visibility';
import AddCategoryModal from '../../configuration/AddCategoryModal';
import DeleteCategoryDialog from '../../configuration/DeleteCategoryDialog';
import CategorySamplesDrawer from '../../configuration/CategorySamplesDrawer';
import notify from '../../../utils/notifier';
import DocuwareTemplateService from '../../../services/DocuwareTemplateService';

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: '24px',
    height: 'calc(100vh - 130px)',
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
  },
  toolbarRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  searchField: {
    width: 320,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: 14,
      color: '#1E293B',
    },
  },
  addButton: {
    backgroundColor: '#00445D',
    color: '#FFFFFF',
    borderRadius: '24px',
    padding: '8px 20px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#03374A' },
  },
  tableWrapper: {
    background: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: 'none',
  },
  table: {
    minWidth: 700,
    borderCollapse: 'collapse',
    '& th': {
      backgroundColor: '#F9FAFB',
      color: '#1E293B',
      fontWeight: 500,
      fontSize: 14,
      textAlign: 'left',
      borderBottom: '1px solid #E5E7EB',
      padding: '12px 16px',
    },
    '& td': {
      fontSize: 14,
      color: '#1E293B',
      borderBottom: '1px solid #E5E7EB',
      padding: '12px 16px',
      verticalAlign: 'middle',
    },
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "#FFFFFF",
    },
    "& tbody tr:nth-of-type(even)": {
      backgroundColor: "#F8FBFF",
    },
    "& tbody tr:hover": {
      backgroundColor: "#F3F4F6",
    },
  },
  actionIcon: {
    color: '#4F4F4F',
    '&:hover': { color: '#00445D' },
  },
  emptyRow: {
    color: '#9CA3AF',
    textAlign: 'center',
    padding: '24px',
  },
  sampleCountChip: {
    backgroundColor: '#E9F4FA',
    color: '#00445D',
    fontSize: 12,
    height: 24,
    fontWeight: 500,
  },
  categoryName: {
    fontWeight: 500,
    color: '#1E293B',
  },
  description: {
    color: '#6B7280',
    fontSize: 13,
  },
  dateText: {
    color: '#6B7280',
    fontSize: 13,
  },
}));

export default function Categories({ currentCompany }) {
  const classes = useStyles();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const searchTimeout = React.useRef(null);

  // Modal states
  const [openAddModal, setOpenAddModal] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [editInitialData, setEditInitialData] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = React.useState('');

  // Samples drawer state
  const [samplesDrawerOpen, setSamplesDrawerOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState(null);

  const fetchCategories = React.useCallback(async () => {
    setLoading(true);
    try {
      const resp = await DocuwareTemplateService.getCategories();
      const data = resp?.data;
      let categories = [];

      if (data?.categories && Array.isArray(data.categories)) {
        categories = data.categories;
      } else if (Array.isArray(data)) {
        categories = data;
      }

      setRows(categories);
    } catch (err) {
      console.error('Failed to fetch categories', err);
      notify({ type: 'error', message: 'Failed to load categories' });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchCategories();
  }, [fetchCategories, currentCompany]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const filteredRows = React.useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(row => 
      row.name?.toLowerCase().includes(term) ||
      row.description?.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const handleCreateCategory = async (payload) => {
    const { onStepChange } = payload;
    
    try {
      // Step 1: Create the category
      onStepChange?.('creating_category', 0);
      
      const createResp = await DocuwareTemplateService.createCategory({
        name: payload.name,
        description: payload.description || ''
      });
      
      // API returns: { success: true, category: { id, name, ... } }
      const categoryId = createResp?.data?.category?.id || createResp?.data?.id || createResp?.data?.categoryId;
      
      if (!categoryId) {
        console.error('Create category response:', createResp?.data);
        throw new Error('Category created but no ID returned');
      }

      // Step 2: Upload all sample files
      if (payload.sampleFiles && payload.sampleFiles.length > 0) {
        onStepChange?.('uploading_samples', 0);
        
        try {
          const uploadResp = await DocuwareTemplateService.uploadCategorySample(categoryId, payload.sampleFiles);
          
          onStepChange?.('processing', 100);
          
          const data = uploadResp?.data;
          if (data?.failed && data.failed.length > 0) {
            const failedCount = data.failed.length;
            const successCount = payload.sampleFiles.length - failedCount;
            notify({ 
              type: 'warning', 
              message: `Category created. ${successCount} samples uploaded, ${failedCount} failed.` 
            });
          } else {
            notify({ 
              type: 'success', 
              message: `Category created with ${payload.sampleFiles.length} samples` 
            });
          }
        } catch (uploadErr) {
          console.error('Error uploading samples', uploadErr);
          notify({ 
            type: 'warning', 
            message: 'Category created but sample upload failed. You can add samples later.' 
          });
        }
      } else {
        notify({ type: 'success', message: 'Category created successfully' });
      }

      await fetchCategories();
      setOpenAddModal(false);
    } catch (err) {
      console.error('Error creating category', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to create category';
      notify({ type: 'error', message: errorMsg });
      throw err;
    }
  };

  const handleUpdateCategory = async (payload) => {
    try {
      const categoryId = editInitialData?.id;
      await DocuwareTemplateService.updateCategory(categoryId, {
        name: payload.name,
        description: payload.description || ''
      });
      await fetchCategories();
      notify({ type: 'success', message: 'Category updated successfully' });
      setEditModalOpen(false);
      setEditInitialData(null);
    } catch (err) {
      console.error('Error updating category', err);
      const errorMsg = err?.response?.data?.message || 'Failed to update category';
      notify({ type: 'error', message: errorMsg });
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await DocuwareTemplateService.deleteCategory(selectedCategoryId);
      await fetchCategories();
      notify({ type: 'success', message: 'Category deleted successfully' });
    } catch (err) {
      console.error('Error deleting category', err);
      const errorMsg = err?.response?.data?.message || 'Failed to delete category';
      notify({ type: 'error', message: errorMsg });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCategoryId(null);
      setSelectedCategoryName('');
    }
  };

  const handleOpenSamplesDrawer = (category) => {
    setSelectedCategory(category);
    setSamplesDrawerOpen(true);
  };

  const handleCloseSamplesDrawer = () => {
    setSamplesDrawerOpen(false);
    setSelectedCategory(null);
    fetchCategories();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbarRow}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search Category"
          value={searchTerm}
          onChange={handleSearchChange}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm && (
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />

        <Button
          className={classes.addButton}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddModal(true)}
        >
          Add Category
        </Button>
      </Box>

      <div className={classes.tableWrapper}>
        <TableContainer>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Category Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Samples</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <span className={classes.categoryName}>{row.name}</span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.description}>
                      {row.description || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.sampleCount || 0}
                      size="small"
                      className={classes.sampleCountChip}
                      onClick={() => handleOpenSamplesDrawer(row)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <span className={classes.dateText}>
                      {formatDate(row.createdAt || row.created_at)}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      className={classes.actionIcon}
                      onClick={() => handleOpenSamplesDrawer(row)}
                      title="View Samples"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={classes.actionIcon}
                      onClick={() => {
                        setEditInitialData(row);
                        setEditModalOpen(true);
                      }}
                      title="Edit Category"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={classes.actionIcon}
                      onClick={() => {
                        setSelectedCategoryId(row.id);
                        setSelectedCategoryName(row.name);
                        setDeleteDialogOpen(true);
                      }}
                      title="Delete Category"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className={classes.emptyRow}>
                    {loading ? 'Loading...' : 'No categories found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Add Category Modal */}
      <AddCategoryModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onCreate={handleCreateCategory}
      />

      {/* Edit Category Modal */}
      <AddCategoryModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditInitialData(null);
        }}
        isEdit
        initialData={editInitialData}
        onSave={handleUpdateCategory}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteCategoryDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedCategoryId(null);
          setSelectedCategoryName('');
        }}
        onConfirm={handleDeleteCategory}
        categoryName={selectedCategoryName}
      />

      {/* Samples Drawer */}
      <CategorySamplesDrawer
        open={samplesDrawerOpen}
        onClose={handleCloseSamplesDrawer}
        category={selectedCategory}
      />
    </Box>
  );
}
